import React, { ChangeEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { Button, CircularProgress, DialogContent, Grid2, IconButton, TextField, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { LocalizationProvider, DateTimePicker, renderTimeViewClock } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/sr';
import { ArticleDto, GoodsStatus, SiteDto, SiteType } from '../../../api/ClientApi';
import { createExpressOrder } from '../../../api/Extensions';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { RootState } from '../../../redux/reducers';
import { importSites } from '../../../redux/actions/siteActions';
import { importAllGoods } from '../../../redux/actions/goodsActions';
import { importArticles } from '../../../redux/actions/articleActions';
import { importApprovedOrderGoodsItems } from '../../../redux/actions/orderGoodsItemsActions';
import { addOrUpdateOrder } from '../../../redux/actions/orderActions';
import { isEmployeeProjectManager, isEmployeeWarehouseManager } from '../../../redux/selectors/employeeSelector';
import SiteAutocomplete from '../../../common/components/autocomplete/SiteAutocomplete';
import ArticleAutocomplete from '../../../common/components/autocomplete/ArticleAutocomplete';
import { DimensionTypeDimensionSR } from '../../../common/Types';

import { FormActions, FormDialogTitle, FormSection } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type ExpressOrderFormProps = {
    open: boolean;
    closeForm(): void;
};

type ExpressItemRow = {
    key: number;
    articleId?: number;
    quantity?: number;
};

/**
 * Ubrzani prenos: roba je već fizički prebačena, pa inicijator u jednom koraku beleži nalog,
 * prenosnicu, otpremnicu i rezervaciju. Nalog završi u statusu "U transportu", a magacioner
 * destinacije ga preuzima kao i svaki drugi.
 */
export default function ExpressOrderForm(props: ExpressOrderFormProps): ReactElement {
    const { open, closeForm } = props;

    const dispatch = useAppDispatch();

    const sites = useAppSelector((state: RootState) => state.sites.sites);
    const goods = useAppSelector((state: RootState) => state.goods.goods);
    const articles = useAppSelector((state: RootState) => state.articles.articles);
    const approvedOrderGoodsItems = useAppSelector((state: RootState) => state.orderGoodsItems.approvedOrderGoodsItems);
    const employeeId = useAppSelector((state: RootState) => state.loggedEmployee.basicEmployee?.id);
    const isWarehouseManager = useAppSelector(isEmployeeWarehouseManager);
    const isProjectManager = useAppSelector(isEmployeeProjectManager);

    const [originSiteId, setOriginSiteId] = useState<number | undefined>(undefined);
    const [destinationSiteId, setDestinationSiteId] = useState<number | undefined>(undefined);
    const [executionDate, setExecutionDate] = useState<Dayjs | null>(dayjs());
    const [description, setDescription] = useState<string>('');
    const [driverName, setDriverName] = useState<string>('');
    const [driverUniqueNumber, setDriverUniqueNumber] = useState<string>('');
    const [vehicleLicensePlateNum, setVehicleLicensePlateNum] = useState<string>('');
    const [rows, setRows] = useState<ExpressItemRow[]>([{ key: 1 }]);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        if (!sites) {
            dispatch(importSites());
        }
        if (!goods) {
            dispatch(importAllGoods());
        }
        if (!articles) {
            dispatch(importArticles());
        }
        if (!approvedOrderGoodsItems) {
            dispatch(importApprovedOrderGoodsItems());
        }
    }, [dispatch, open]);

    // Samo site-ovi za koje bi korisnik u redovnom toku dao najjače odobrenje:
    // WM za magacine firme, PM za gradilišta/depoe svojih projekata.
    const originOptions = useMemo<SiteDto[]>(() => {
        if (!sites) {
            return [];
        }
        return sites.filter((s) => {
            if (s.siteType === SiteType.Warehouse) {
                return isWarehouseManager;
            }
            return isProjectManager && !!employeeId && !!s.project?.projectManagersAzObjIds?.includes(employeeId);
        });
    }, [sites, isWarehouseManager, isProjectManager, employeeId]);

    const destinationOptions = useMemo<SiteDto[]>(
        () => (sites ?? []).filter((s) => s.id !== originSiteId),
        [sites, originSiteId],
    );

    // Raspoloživo po artiklu na origin site-u: na stanju minus "meke" rezervacije odobrenih naloga.
    const availableByArticle = useMemo<Map<number, number>>(() => {
        const result = new Map<number, number>();
        if (!originSiteId || !goods) {
            return result;
        }
        goods
            .filter((g) => g.siteId === originSiteId && g.goodsStatus === GoodsStatus.InStock && !!g.article?.id)
            .forEach((g) => {
                const id = g.article!.id!;
                result.set(id, (result.get(id) ?? 0) + (g.quantity ?? 0));
            });
        approvedOrderGoodsItems
            ?.filter((ogi) => ogi.order?.originSiteId === originSiteId && !!ogi.articleId)
            .forEach((ogi) => {
                const id = ogi.articleId!;
                if (result.has(id)) {
                    result.set(id, result.get(id)! - (ogi.quantity ?? 0));
                }
            });
        return result;
    }, [goods, approvedOrderGoodsItems, originSiteId]);

    const articleOptions = useMemo<ArticleDto[]>(
        () => (articles ?? []).filter((a) => !!a.id && (availableByArticle.get(a.id) ?? 0) > 0),
        [articles, availableByArticle],
    );

    const remainingForRow = (row: ExpressItemRow): number | undefined => {
        if (!row.articleId) {
            return undefined;
        }
        const otherRowsQuantity = rows
            .filter((r) => r.key !== row.key && r.articleId === row.articleId)
            .reduce((acc, r) => acc + (r.quantity ?? 0), 0);
        return (availableByArticle.get(row.articleId) ?? 0) - otherRowsQuantity;
    };

    const isRowValid = (row: ExpressItemRow): boolean => {
        const remaining = remainingForRow(row);
        return (
            !!row.articleId &&
            !!row.quantity &&
            row.quantity > 0 &&
            remaining !== undefined &&
            row.quantity <= remaining
        );
    };

    const isFormValid =
        !!originSiteId &&
        !!destinationSiteId &&
        originSiteId !== destinationSiteId &&
        !!executionDate &&
        description.trim() !== '' &&
        rows.length > 0 &&
        rows.every(isRowValid);

    const resetForm = () => {
        setOriginSiteId(undefined);
        setDestinationSiteId(undefined);
        setExecutionDate(dayjs());
        setDescription('');
        setDriverName('');
        setDriverUniqueNumber('');
        setVehicleLicensePlateNum('');
        setRows([{ key: 1 }]);
        setError(null);
        setSaving(false);
    };

    const handleClose = () => {
        resetForm();
        closeForm();
    };

    const handleOriginSelect = (siteId?: number) => {
        setOriginSiteId(siteId);
        setRows([{ key: 1 }]);
        if (siteId !== undefined && siteId === destinationSiteId) {
            setDestinationSiteId(undefined);
        }
    };

    const updateRow = (key: number, patch: Partial<ExpressItemRow>) => {
        setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    };

    const addRow = () => {
        setRows((prev) => [...prev, { key: (prev[prev.length - 1]?.key ?? 0) + 1 }]);
    };

    const removeRow = (key: number) => {
        setRows((prev) => (prev.length === 1 ? [{ key: 1 }] : prev.filter((r) => r.key !== key)));
    };

    const handleSave = async () => {
        if (!isFormValid || !originSiteId || !destinationSiteId || !executionDate) {
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const created = await createExpressOrder({
                originSiteId,
                destinationSiteId,
                executionDate: executionDate.toDate(),
                description: description.trim(),
                driverName: driverName.trim() || undefined,
                driverUniqueNumber: driverUniqueNumber.trim() || undefined,
                vehicleLicensePlateNum: vehicleLicensePlateNum.trim() || undefined,
                items: rows.map((r) => ({ articleId: r.articleId!, quantity: r.quantity! })),
            });
            dispatch(addOrUpdateOrder(created));
            // Roba na origin site-u je rezervisana; najjednostavnije je osvežiti celu listu robe.
            dispatch(importAllGoods());
            handleClose();
        } catch (err: any) {
            const code: string | undefined = err?.messageCode;
            setError(
                code === 'BAD_INSUFFICIENT_STOCK'
                    ? (err?.messageValue ?? 'Nema dovoljno robe na stanju.')
                    : code === 'BAD_UNAUTHORIZED_USER'
                      ? 'Nemate ovlašćenje da ubrzano prenesete robu sa ovog skladišta.'
                      : 'Došlo je do greške pri čuvanju ubrzanog prenosa.',
            );
            setSaving(false);
        }
    };

    const articleDimension = (articleId?: number): string => {
        const article = articles?.find((a) => a.id === articleId);
        return article?.dimensionType ? DimensionTypeDimensionSR[article.dimensionType] : '';
    };

    return (
        <ResponsiveDialog scroll="body" maxWidth="md" open={open} onClose={handleClose} disableEscapeKeyDown>
            <FormDialogTitle
                accent="warning"
                onClose={handleClose}
                subtitle="Za robu koja je već fizički prebačena. Nalog se odmah beleži kao poslat (prenosnica i otpremnica se prave automatski), a magacioner odredišta ga potvrđuje kroz „Preuzmi robu“."
            >
                Ubrzani prenos robe
            </FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 4 } }}>
                <Grid2 container spacing={2} style={{ maxWidth: 1000, margin: 'auto' }}>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <SiteAutocomplete
                            isRequired
                            title="Odakle (izvorno skladište)"
                            sites={originOptions}
                            selectedSiteId={originSiteId}
                            selectSite={handleOriginSelect}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <SiteAutocomplete
                            isRequired
                            title="Kuda (odredište)"
                            sites={destinationOptions}
                            selectedSiteId={destinationSiteId}
                            selectSite={setDestinationSiteId}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="sr">
                            <DateTimePicker
                                label="Kada je roba prebačena"
                                viewRenderers={{
                                    hours: renderTimeViewClock,
                                    minutes: renderTimeViewClock,
                                    seconds: renderTimeViewClock,
                                }}
                                value={executionDate}
                                onChange={(value) => setExecutionDate(value)}
                                disableFuture
                            />
                        </LocalizationProvider>
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 6 }} />
                    <Grid2 size={{ xs: 12 }}>
                        <TextField
                            required
                            fullWidth
                            multiline
                            rows={2}
                            label="Obrazloženje (zašto je preskočen redovan tok)"
                            value={description}
                            error={description.trim() === ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
                        />
                    </Grid2>

                    <FormSection title="Roba" />
                    {rows.map((row) => {
                        const remaining = remainingForRow(row);
                        const quantityError = !!row.articleId && !isRowValid(row);
                        return (
                            <React.Fragment key={row.key}>
                                <Grid2 size={{ xs: 12, sm: 7 }}>
                                    <ArticleAutocomplete
                                        isRequired
                                        articles={articleOptions}
                                        selectedArticleId={row.articleId}
                                        selectArticle={(articleId) => updateRow(row.key, { articleId })}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 9, sm: 4 }}>
                                    <TextField
                                        required
                                        fullWidth
                                        type="number"
                                        label="Količina"
                                        disabled={!row.articleId}
                                        error={quantityError}
                                        helperText={
                                            row.articleId
                                                ? `Raspoloživo: ${remaining ?? 0} ${articleDimension(row.articleId)}`
                                                : ' '
                                        }
                                        value={row.quantity ?? ''}
                                        slotProps={{ htmlInput: { inputMode: 'decimal', min: 0, step: 'any' } }}
                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                            updateRow(row.key, {
                                                quantity: e.target.value === '' ? undefined : +e.target.value,
                                            })
                                        }
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 3, sm: 1 }} sx={{ display: 'flex', alignItems: 'center' }}>
                                    <IconButton onClick={() => removeRow(row.key)} aria-label="Ukloni red">
                                        <DeleteIcon />
                                    </IconButton>
                                </Grid2>
                            </React.Fragment>
                        );
                    })}
                    <Grid2 size={{ xs: 12 }}>
                        <Button
                            variant="text"
                            startIcon={<AddIcon />}
                            onClick={addRow}
                            disabled={!originSiteId}
                            sx={{ textTransform: 'none' }}
                        >
                            Dodaj artikal
                        </Button>
                    </Grid2>

                    <FormSection title="Prevoz (opciono)" />
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                        <TextField
                            fullWidth
                            label="Vozač"
                            value={driverName}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setDriverName(e.target.value)}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                        <TextField
                            fullWidth
                            label="Br. lične karte vozača"
                            value={driverUniqueNumber}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setDriverUniqueNumber(e.target.value)}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, sm: 4 }}>
                        <TextField
                            fullWidth
                            label="Registarske tablice"
                            value={vehicleLicensePlateNum}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => setVehicleLicensePlateNum(e.target.value)}
                        />
                    </Grid2>

                    {error && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography color="error">*{error}</Typography>
                        </Grid2>
                    )}
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Zabeleži prenos"
                        color="warning"
                        disabled={!isFormValid}
                        pending={saving}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
